import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { links } from "./SuperAdminPages";
import api, { apiErrorMessage } from "../../lib/api";
import { toast } from "sonner";

const money = (amount = 0, currency = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: String(currency || "INR").toUpperCase(),
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
const date = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Not scheduled";
const norm = (value) => String(value || "").toLowerCase();
const paid = (row) =>
  ["paid", "approved", "completed", "success", "succeeded"].includes(
    norm(row.payment_status),
  );
const tabs = [
  { id: "due", label: "Due soon" },
  { id: "overdue", label: "Overdue" },
  { id: "pending", label: "Payment pending" },
  { id: "paid", label: "Renewed in last 30 days" },
  { id: "all", label: "All clinics" },
];
const matches = (row, tab) =>
  tab === "overdue"
    ? row.days_to_renewal != null && row.days_to_renewal < 0
    : tab === "pending"
      ? ["pending", "initiated", "processing", "payment_pending"].includes(
          norm(row.payment_status),
        )
      : tab === "paid"
        ? paid(row) && row.last_payment_at && Date.now() - new Date(row.last_payment_at).getTime() <= 30 * 86400000
        : tab === "due"
          ? row.days_to_renewal != null &&
            row.days_to_renewal >= 0 &&
            row.days_to_renewal <= 30
          : true;

function Status({ row }) {
  const overdue = row.days_to_renewal != null && row.days_to_renewal < 0,
    text = overdue
      ? "Overdue"
      : paid(row)
        ? "Paid"
        : row.payment_status || row.subscription_status || "Pending",
    color = overdue
      ? "bg-red-50 text-red-700"
      : paid(row)
        ? "bg-emerald-50 text-emerald-700"
        : "bg-amber-50 text-amber-700";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${color}`}
    >
      {text}
    </span>
  );
}

export default function PlatformBillingPage() {
  const [renewals, setRenewals] = useState([]),
    [tab, setTab] = useState("due"),
    [query, setQuery] = useState(""),
    [loading, setLoading] = useState(true),
    [paying, setPaying] = useState(""),
    [error, setError] = useState(""),
    [offline, setOffline] = useState(null),
    [offlineForm, setOfflineForm] = useState({
      method: "cash",
      amount: "",
      reference: "",
      received_on: new Date().toISOString().slice(0, 10),
      notes: "",
    });
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/admin/renewals?window=all");
      setRenewals(r.data?.rows || []);
    } catch (e) {
      const message = apiErrorMessage(e);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const filtered = useMemo(
    () =>
      renewals.filter(
        (row) =>
          matches(row, tab) &&
          `${row.clinic_name || ""} ${row.plan_name || ""} ${row.clinic_id || ""}`
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
      ),
    [renewals, tab, query],
  );
  const counts = useMemo(
    () =>
      Object.fromEntries(
        tabs.map((item) => [
          item.id,
          renewals.filter((row) => matches(row, item.id)).length,
        ]),
      ),
    [renewals],
  );
  const collect = async (row) => {
    setPaying(row.clinic_id);
    try {
      const result = (
        await api.post(
          `/subscriptions/clinic/${row.clinic_id}/payment/create-order`,
          {
            amount: Number(row.amount || 0) || undefined,
            currency: row.currency || "INR",
            idempotency_key: `renewal-${row.clinic_id}-${row.renewal_at || "current"}`,
          },
        )
      ).data;
      if (result.status !== "pending")
        throw new Error(result.reason || "The payment gateway is unavailable.");
      if (!window.Razorpay)
        throw new Error("Razorpay checkout is not loaded in this browser.");
      new window.Razorpay({
        ...result.gateway_payload,
        name: "MediGenie subscription",
        description: `${row.clinic_name || "Clinic"} · ${row.plan_name || "Plan"}`,
        handler: async (payment) => {
          await api.post(
            `/subscriptions/clinic/${row.clinic_id}/payment/verify`,
            {
              transaction_id: result.transaction_id,
              provider_payment_id: payment.razorpay_payment_id,
              provider_signature: payment.razorpay_signature,
            },
          );
          toast.success("Subscription payment confirmed");
          load();
        },
      }).open();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          e.message ||
          "Payment could not be started",
      );
    } finally {
      setPaying("");
    }
  };
  const openOffline = (row) => {
    setOffline(row);
    setOfflineForm({
      method: "cash",
      amount: String(row.amount || ""),
      reference: "",
      received_on: new Date().toISOString().slice(0, 10),
      notes: "",
    });
  };
  const recordOffline = async () => {
    if (!offlineForm.reference.trim())
      return toast.error("Enter a receipt or bank reference");
    setPaying(offline.clinic_id);
    try {
      await api.post(
        `/subscriptions/clinic/${offline.clinic_id}/payment/offline`,
        {
          ...offlineForm,
          amount: Number(offlineForm.amount),
          received_on: new Date(
            `${offlineForm.received_on}T12:00:00Z`,
          ).toISOString(),
        },
      );
      toast.success("Offline payment recorded and renewal extended");
      setOffline(null);
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Offline payment could not be recorded",
      );
    } finally {
      setPaying("");
    }
  };
  return (
    <DashboardLayout links={links} title="Renewals & Payments">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-mg-navy">
            Subscription renewals
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Monitor clinic packages, renewal dates, and gateway payments.
            Platform invoices are retained below as read-only history.
          </p>
        </div>
        <button className="mg-btn-secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>
      {error && (
        <div
          className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          role="alert"
        >
          Unable to load renewals: {error}{" "}
          <button className="ml-3 font-semibold underline" onClick={load}>
            Retry
          </button>
        </div>
      )}
      <div
        className="mb-4 flex flex-wrap gap-2"
        role="tablist"
        aria-label="Renewal status"
      >
        {tabs.map((item) => (
          <button
            key={item.id}
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${tab === item.id ? "bg-mg-navy text-white" : "border border-slate-200 bg-white text-slate-600"}`}
          >
            {item.label}{" "}
            <span className="ml-1 opacity-75">{counts[item.id] || 0}</span>
          </button>
        ))}
      </div>
      <div className="mg-card mb-6 overflow-hidden">
        <div className="border-b border-slate-100 p-4">
          <label className="sr-only" htmlFor="renewal-search">
            Search clinics or packages
          </label>
          <input
            id="renewal-search"
            className="mg-input max-w-md"
            placeholder="Search clinic, package, or ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {loading ? (
          <p className="p-8 text-center text-slate-500" role="status">
            Loading renewals…
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <th className="p-3">Clinic</th>
                  <th>Package</th>
                  <th>Renewal</th>
                  <th>Amount</th>
                  <th>Last payment</th>
                  <th>Status</th>
                  <th>
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr className="border-b border-slate-100" key={row.clinic_id}>
                    <td className="p-3">
                      <div className="font-semibold text-mg-navy">
                        {row.clinic_name || row.clinic_id}
                      </div>
                      <div className="text-xs text-slate-400">
                        {row.clinic_id}
                      </div>
                    </td>
                    <td>
                      {row.plan_name || "—"}
                      <div className="text-xs capitalize text-slate-400">
                        {row.billing_cycle || "monthly"}
                      </div>
                    </td>
                    <td>
                      {date(row.renewal_at)}
                      <div
                        className={`text-xs ${row.days_to_renewal < 0 ? "text-red-600" : "text-slate-400"}`}
                      >
                        {row.days_to_renewal == null
                          ? ""
                          : row.days_to_renewal < 0
                            ? `${Math.abs(row.days_to_renewal)} days overdue`
                            : `${row.days_to_renewal} days remaining`}
                      </div>
                    </td>
                    <td>{money(row.amount, row.currency)}</td>
                    <td>{date(row.last_payment_at)}</td>
                    <td>
                      <Status row={row} />
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        {!paid(row) && (
                          <>
                            <button
                              className="mg-btn-secondary whitespace-nowrap text-xs"
                              disabled={paying === row.clinic_id}
                              onClick={() => openOffline(row)}
                            >
                              Record offline
                            </button>
                            <button
                              className="mg-btn-primary whitespace-nowrap text-xs"
                              disabled={paying === row.clinic_id}
                              onClick={() => collect(row)}
                            >
                              {paying === row.clinic_id
                                ? "Starting…"
                                : "Collect online"}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-slate-400">
                      No clinics match this view.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {offline && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <section className="mg-card w-full max-w-lg">
            <h2 className="text-xl font-black text-mg-navy">
              Record offline payment
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {offline.clinic_name} · {offline.plan_name}
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <label className="text-xs font-bold">
                Payment method
                <select
                  className="mg-input w-full mt-1"
                  value={offlineForm.method}
                  onChange={(e) =>
                    setOfflineForm({ ...offlineForm, method: e.target.value })
                  }
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="text-xs font-bold">
                Amount
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="mg-input w-full mt-1"
                  value={offlineForm.amount}
                  onChange={(e) =>
                    setOfflineForm({ ...offlineForm, amount: e.target.value })
                  }
                />
              </label>
              <label className="text-xs font-bold">
                Received date
                <input
                  type="date"
                  className="mg-input w-full mt-1"
                  value={offlineForm.received_on}
                  onChange={(e) =>
                    setOfflineForm({
                      ...offlineForm,
                      received_on: e.target.value,
                    })
                  }
                />
              </label>
              <label className="text-xs font-bold">
                Receipt / transaction reference
                <input
                  className="mg-input w-full mt-1"
                  value={offlineForm.reference}
                  onChange={(e) =>
                    setOfflineForm({
                      ...offlineForm,
                      reference: e.target.value,
                    })
                  }
                  placeholder="Required unique reference"
                />
              </label>
              <label className="sm:col-span-2 text-xs font-bold">
                Notes
                <textarea
                  className="mg-input w-full mt-1"
                  rows="3"
                  value={offlineForm.notes}
                  onChange={(e) =>
                    setOfflineForm({ ...offlineForm, notes: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="mg-btn-secondary"
                onClick={() => setOffline(null)}
              >
                Cancel
              </button>
              <button
                className="mg-btn-primary"
                disabled={
                  paying === offline.clinic_id || !Number(offlineForm.amount)
                }
                onClick={recordOffline}
              >
                {paying === offline.clinic_id
                  ? "Recording…"
                  : "Confirm offline payment"}
              </button>
            </div>
          </section>
        </div>
      )}
    </DashboardLayout>
  );
}
